import { ClaimFormType } from 'domains/order/Order.types';
import { getFormGroupType } from 'domains/order/Order.utils';

export interface RulesKey {
  formType: ClaimFormType;
  reason: string;
  option?: string;
}

export const makeKey = ({ formType, reason, option }: RulesKey): string => {
  return `${getFormGroupType(formType)}${reason}${option ?? ''}`;
};

export type FormOption = 'mandatory' | 'optional' | 'hidden';

export interface RulesData {
  orderNumber: FormOption;
  deliveryNumber: FormOption;
  packageNumber: FormOption;
  invoiceNumber: FormOption;
  invoiceLineNumber: FormOption;
  referenceNumberOrdered: FormOption;
  quantityOrdered: FormOption;
  referenceNumberReceived: FormOption;
  quantityReceived: FormOption;
  quantityInvoiced: FormOption;
  quantityClaimed: FormOption;
  amount: FormOption;
  soldToPartyId: FormOption;
  shipToPartyId: FormOption;
  contact: FormOption;
  email: FormOption;
  phoneNumber: FormOption;
  pickupAddress: FormOption;
  pickupTimeSlots: FormOption;
  message: FormOption;
  attachments: FormOption;
  vehicle: FormOption;
  labour: FormOption;
  symptom: FormOption;
}

export interface MultiItemsRulesData {
  referenceNumber: FormOption;
  quantityInvoiced: FormOption;
  quantityClaimed: FormOption;
}

export const RULES_DATA: Record<string, RulesData | undefined> = {
  // Y01 Additional part — ACCEPT (option '1')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y01', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y01 Additional part — DO NOT ACCEPT (option '0')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y01', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y1N Additional part without order/invoice — ACCEPT (option '1')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y1N', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y1N Additional part without order/invoice — DO NOT ACCEPT (option '0')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y1N', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y02 Missing part
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y02' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y03 Defective part
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y03' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y04 Damaged part
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y04' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y05 Wrong part — ACCEPT (option '1')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y05', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'mandatory',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y05 Wrong part — DO NOT ACCEPT (option '0')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y05', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'mandatory',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y06 Recipient error — ACCEPT (option '1')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y06', option: '1' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y06 Recipient error — DO NOT ACCEPT (option '0')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y06', option: '0' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y07 Missing package
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y07' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y08 Missing line
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y08' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y09 Unused part
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y09' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y9A Unused part for exadis references
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y9A' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'mandatory',
    email: 'mandatory',
    phoneNumber: 'mandatory',
    pickupAddress: 'mandatory',
    pickupTimeSlots: 'mandatory',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y10 Wrong price — LESS PAID (option '0')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y10', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'mandatory',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y10 Wrong price — TOO MUCH PAID (option '1')
  [makeKey({ formType: 'DIRECT_DELIVERY', reason: 'Y10', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'mandatory',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'optional',
    email: 'optional',
    phoneNumber: 'optional',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y01 Additional part — ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y01', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y01 Additional part — DO NOT ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y01', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y1N Additional part without order/invoice — ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y1N', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y1N Additional part without order/invoice — DO NOT ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y1N', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y02 Missing part
  [makeKey({ formType: 'FULL_OTC', reason: 'Y02' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y03 Defective part
  [makeKey({ formType: 'FULL_OTC', reason: 'Y03' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y3T Defective part with blocking note
  [makeKey({ formType: 'FULL_OTC', reason: 'Y3T' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y04 Damaged part
  [makeKey({ formType: 'FULL_OTC', reason: 'Y04' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y05 Wrong part — ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y05', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'mandatory',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y05 Wrong part — DO NOT ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y05', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'mandatory',
    quantityReceived: 'mandatory',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y06 Recipient error — ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y06', option: '1' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y06 Recipient error — DO NOT ACCEPT
  [makeKey({ formType: 'FULL_OTC', reason: 'Y06', option: '0' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'mandatory',
    shipToPartyId: 'mandatory',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y07 Missing package
  [makeKey({ formType: 'FULL_OTC', reason: 'Y07' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y08 Missing line
  [makeKey({ formType: 'FULL_OTC', reason: 'Y08' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y09 Unused part
  [makeKey({ formType: 'FULL_OTC', reason: 'Y09' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y9A Unused part for exadis references
  [makeKey({ formType: 'FULL_OTC', reason: 'Y9A' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y9G Warranty for exadis references
  [makeKey({ formType: 'FULL_OTC', reason: 'Y9G' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'hidden',
    vehicle: 'mandatory',
    labour: 'mandatory',
    symptom: 'mandatory',
  },

  // Y10 Wrong price — LESS PAID
  [makeKey({ formType: 'FULL_OTC', reason: 'Y10', option: '0' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'mandatory',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y10 Wrong price — TOO MUCH PAID
  [makeKey({ formType: 'FULL_OTC', reason: 'Y10', option: '1' })]: {
    orderNumber: 'mandatory',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'mandatory',
    referenceNumberOrdered: 'mandatory',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'optional',
    quantityClaimed: 'mandatory',
    amount: 'mandatory',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y04 Damaged part
  [makeKey({ formType: 'MULTI_ITEMS_DIRECT_DELIVERY', reason: 'Y04' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'mandatory',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'hidden',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'hidden',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y07 Missing package
  [makeKey({ formType: 'MULTI_ITEMS_DIRECT_DELIVERY', reason: 'Y07' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'mandatory',
    packageNumber: 'hidden',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'hidden',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'hidden',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y04 Damaged part
  [makeKey({ formType: 'MULTI_ITEMS_FULL_OTC', reason: 'Y04' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'mandatory',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'hidden',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'hidden',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'mandatory',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y07 Missing package
  [makeKey({ formType: 'MULTI_ITEMS_FULL_OTC', reason: 'Y07' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'mandatory',
    invoiceNumber: 'mandatory',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'hidden',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'hidden',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'mandatory',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y09 Exadis return list — read-only claim form (references + return quantities locked)
  [makeKey({ formType: 'MULTI_ITEMS_EXADIS_RETURN', reason: 'Y09' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'hidden',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'hidden',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },

  // Y9A Deposit return list — read-only claim form (references + return quantities locked)
  [makeKey({ formType: 'MULTI_ITEMS_DEPOSIT_RETURN', reason: 'Y9A' })]: {
    orderNumber: 'hidden',
    deliveryNumber: 'hidden',
    packageNumber: 'hidden',
    invoiceNumber: 'hidden',
    invoiceLineNumber: 'hidden',
    referenceNumberOrdered: 'hidden',
    quantityOrdered: 'hidden',
    referenceNumberReceived: 'hidden',
    quantityReceived: 'hidden',
    quantityInvoiced: 'hidden',
    quantityClaimed: 'hidden',
    amount: 'hidden',
    soldToPartyId: 'hidden',
    shipToPartyId: 'hidden',
    contact: 'hidden',
    email: 'hidden',
    phoneNumber: 'hidden',
    pickupAddress: 'hidden',
    pickupTimeSlots: 'hidden',
    message: 'optional',
    attachments: 'optional',
    vehicle: 'hidden',
    labour: 'hidden',
    symptom: 'hidden',
  },
};

export const MULTI_ITEMS_RULES_DATA: Record<string, MultiItemsRulesData | undefined> = {
  // Y04 Damaged part
  [makeKey({ formType: 'MULTI_ITEMS_DIRECT_DELIVERY', reason: 'Y04' })]: {
    referenceNumber: 'mandatory',
    quantityInvoiced: 'mandatory',
    quantityClaimed: 'mandatory',
  },

  // Y07 Missing package
  [makeKey({ formType: 'MULTI_ITEMS_DIRECT_DELIVERY', reason: 'Y07' })]: {
    referenceNumber: 'mandatory',
    quantityInvoiced: 'mandatory',
    quantityClaimed: 'hidden',
  },

  // Y04 Damaged part
  [makeKey({ formType: 'MULTI_ITEMS_FULL_OTC', reason: 'Y04' })]: {
    referenceNumber: 'mandatory',
    quantityInvoiced: 'mandatory',
    quantityClaimed: 'mandatory',
  },

  // Y07 Missing package
  [makeKey({ formType: 'MULTI_ITEMS_FULL_OTC', reason: 'Y07' })]: {
    referenceNumber: 'mandatory',
    quantityInvoiced: 'mandatory',
    quantityClaimed: 'hidden',
  },

  // Y09 Exadis return list — references + invoiced (return) quantities shown read-only, no claimed-quantity input
  [makeKey({ formType: 'MULTI_ITEMS_EXADIS_RETURN', reason: 'Y09' })]: {
    referenceNumber: 'optional',
    quantityInvoiced: 'optional',
    quantityClaimed: 'hidden',
  },

  // Y9A Deposit return list — references + invoiced (return) quantities shown read-only, no claimed-quantity input
  [makeKey({ formType: 'MULTI_ITEMS_DEPOSIT_RETURN', reason: 'Y9A' })]: {
    referenceNumber: 'optional',
    quantityInvoiced: 'optional',
    quantityClaimed: 'hidden',
  },
};
